import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  cn,
  formatNumber,
  formatPercent,
  timeAgo,
  formatDate,
  formatDuration,
  getStatusColor,
} from '@/lib/utils';
import { apiService as mockApi } from '@/api';
import { Card, CardHeader, CardTitle, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Tabs } from '@/components/ui/Tabs';
import { LoadingState, EmptyState } from '@/components/ui/States';
import type { Device, Job, Script, Site, Client, PaginatedResponse } from '@/types';
import {
  MapPin,
  Maximize2,
  Minimize2,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Search,
  Filter,
  Building2,
  Server,
  Globe,
  SlidersHorizontal,
  ChevronRight,
  X,
  ExternalLink,
  Layers,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Info,
  Laptop,
  Activity,
  Shield,
  Clock,
  Navigation,
} from 'lucide-react';

interface Cluster {
  id: string;
  xPct: number;
  yPct: number;
  sites: Site[];
  totalDevices: number;
  worstStatus: 'online' | 'warning' | 'critical' | 'offline';
  statusCounts: Record<string, number>;
}

// Convert latitude and longitude to percentage on an equirectangular map
function latLngToPercent(lat: number, lng: number) {
  const clampedLat = Math.max(-60, Math.min(80, lat));
  const x = ((lng + 180) / 360) * 100;
  const y = ((85 - clampedLat) / 145) * 100;
  return {
    xPct: Math.max(3, Math.min(97, x)),
    yPct: Math.max(4, Math.min(96, y)),
  };
}

export function MapView() {
  const navigate = useNavigate();
  const mapRef = useRef<HTMLDivElement>(null);

  // State
  const [sites, setSites] = useState<Site[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedClient, setSelectedClient] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Map settings & interaction state
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [hoveredSiteId, setHoveredSiteId] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const [clusteringEnabled, setClusteringEnabled] = useState<boolean>(true);
  const [showGrid, setShowGrid] = useState<boolean>(true);
  const [siteModalOpen, setSiteModalOpen] = useState<boolean>(false);
  const [modalSite, setModalSite] = useState<Site | null>(null);

  // Fetch initial data
  useEffect(() => {
    let isMounted = true;
    async function fetchData() {
      setLoading(true);
      try {
        const [sitesData, clientsData, devicesResp] = await Promise.all([
          mockApi.getSites(),
          mockApi.getClients(),
          mockApi.getDevices({ pageSize: 500 }),
        ]);
        if (isMounted) {
          setSites(sitesData);
          setClients(clientsData);
          setDevices(devicesResp.data);
          if (sitesData.length > 0) {
            setSelectedSiteId(sitesData[0].id);
          }
        }
      } catch (err) {
        console.error('Failed to load map view data:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    fetchData();
    return () => {
      isMounted = false;
    };
  }, []);

  // Map site IDs to their devices and overall status breakdown
  const siteDeviceMap = useMemo(() => {
    const map = new Map<string, { devices: Device[]; counts: Record<string, number>; worstStatus: string }>();
    sites.forEach((site) => {
      const siteDevs = devices.filter((d) => d.siteId === site.id || d.site === site.name);
      const counts: Record<string, number> = { online: 0, warning: 0, critical: 0, offline: 0 };
      siteDevs.forEach((d) => {
        counts[d.status] = (counts[d.status] || 0) + 1;
      });

      let worstStatus = 'online';
      if (counts.critical > 0) worstStatus = 'critical';
      else if (counts.warning > 0) worstStatus = 'warning';
      else if (counts.offline > 0 && counts.online === 0) worstStatus = 'offline';
      else if (counts.offline > 0) worstStatus = 'warning';

      map.set(site.id, { devices: siteDevs, counts, worstStatus });
    });
    return map;
  }, [sites, devices]);

  // Filtered sites based on client, status, and search query
  const filteredSites = useMemo(() => {
    return sites.filter((site) => {
      // Client filter
      if (selectedClient !== 'all' && site.clientId !== selectedClient) {
        const clientObj = clients.find((c) => c.id === selectedClient);
        if (clientObj && site.name !== clientObj.name) return false;
      }

      const siteInfo = siteDeviceMap.get(site.id);
      const worstStatus = siteInfo?.worstStatus || 'online';

      // Status filter
      if (statusFilter !== 'all') {
        if (statusFilter === 'online' && worstStatus !== 'online') return false;
        if (statusFilter === 'warning' && worstStatus !== 'warning') return false;
        if (statusFilter === 'critical' && worstStatus !== 'critical') return false;
        if (statusFilter === 'offline' && worstStatus !== 'offline') return false;
      }

      // Search filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesSite =
          site.name.toLowerCase().includes(q) ||
          site.city.toLowerCase().includes(q) ||
          site.country.toLowerCase().includes(q) ||
          site.address.toLowerCase().includes(q);

        const matchesDevice = siteInfo?.devices.some(
          (d) =>
            d.hostname.toLowerCase().includes(q) ||
            d.publicIp.includes(q) ||
            d.localIp.includes(q)
        );

        if (!matchesSite && !matchesDevice) return false;
      }

      return true;
    });
  }, [sites, clients, selectedClient, statusFilter, searchQuery, siteDeviceMap]);

  // Calculate site clusters
  const clusters = useMemo(() => {
    if (!clusteringEnabled || zoomLevel > 1.8) {
      return filteredSites.map((site) => {
        const { xPct, yPct } = latLngToPercent(site.lat, site.lng);
        const info = siteDeviceMap.get(site.id);
        return {
          id: site.id,
          xPct,
          yPct,
          sites: [site],
          totalDevices: site.deviceCount || info?.devices.length || 0,
          worstStatus: (info?.worstStatus || 'online') as any,
          statusCounts: info?.counts || { online: 0, warning: 0, critical: 0, offline: 0 },
        };
      });
    }

    const clusterList: Cluster[] = [];
    const thresholdPct = 7 / zoomLevel;

    filteredSites.forEach((site) => {
      const { xPct, yPct } = latLngToPercent(site.lat, site.lng);
      const info = siteDeviceMap.get(site.id);

      let addedToCluster = false;
      for (const cluster of clusterList) {
        const dx = cluster.xPct - xPct;
        const dy = cluster.yPct - yPct;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < thresholdPct) {
          cluster.sites.push(site);
          cluster.totalDevices += site.deviceCount || info?.devices.length || 0;
          // recalculate center
          cluster.xPct = (cluster.xPct * (cluster.sites.length - 1) + xPct) / cluster.sites.length;
          cluster.yPct = (cluster.yPct * (cluster.sites.length - 1) + yPct) / cluster.sites.length;

          // Merge status counts
          if (info?.counts) {
            Object.entries(info.counts).forEach(([k, v]) => {
              cluster.statusCounts[k] = (cluster.statusCounts[k] || 0) + v;
            });
          }

          if (info?.worstStatus === 'critical') cluster.worstStatus = 'critical';
          else if (info?.worstStatus === 'warning' && cluster.worstStatus !== 'critical') cluster.worstStatus = 'warning';

          addedToCluster = true;
          break;
        }
      }

      if (!addedToCluster) {
        clusterList.push({
          id: `cluster-${site.id}`,
          xPct,
          yPct,
          sites: [site],
          totalDevices: site.deviceCount || info?.devices.length || 0,
          worstStatus: (info?.worstStatus || 'online') as any,
          statusCounts: { ...(info?.counts || { online: 0, warning: 0, critical: 0, offline: 0 }) },
        });
      }
    });

    return clusterList;
  }, [filteredSites, clusteringEnabled, zoomLevel, siteDeviceMap]);

  // Currently selected site
  const selectedSite = useMemo(() => {
    return sites.find((s) => s.id === selectedSiteId) || filteredSites[0] || null;
  }, [sites, selectedSiteId, filteredSites]);

  // Devices for selected site
  const selectedSiteDevices = useMemo(() => {
    if (!selectedSite) return [];
    const info = siteDeviceMap.get(selectedSite.id);
    return info ? info.devices : [];
  }, [selectedSite, siteDeviceMap]);

  // Global summary statistics
  const mapStats = useMemo(() => {
    let online = 0;
    let warning = 0;
    let critical = 0;
    let offline = 0;

    devices.forEach((d) => {
      if (d.status === 'online') online++;
      else if (d.status === 'warning') warning++;
      else if (d.status === 'critical') critical++;
      else if (d.status === 'offline') offline++;
    });

    return {
      totalSites: sites.length,
      visibleSites: filteredSites.length,
      totalDevices: devices.length,
      online,
      warning,
      critical,
      offline,
    };
  }, [sites.length, filteredSites.length, devices]);

  // Handlers
  const handleFitToDevices = () => {
    setZoomLevel(1);
  };

  const handleOpenSiteModal = (site: Site) => {
    setModalSite(site);
    setSiteModalOpen(true);
  };

  if (loading) {
    return (
      <div className="p-6">
        <LoadingState rows={8} />
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col h-[calc(100vh-4rem)] bg-[var(--bg)]', isFullScreen && 'fixed inset-0 z-50 h-screen w-screen')}>
      {/* Top Header & Filter Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3 border-b border-[var(--border)] bg-[var(--panel)] shrink-0">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-[var(--accent)]" />
            <h1 className="text-base font-semibold text-[var(--text-primary)]">Global Infrastructure Map</h1>
          </div>
          <Badge variant="default" className="bg-[var(--elevated)] text-[var(--text-secondary)]">
            {mapStats.visibleSites} of {mapStats.totalSites} Sites
          </Badge>
          <Badge variant="default" className="bg-[var(--elevated)] text-[var(--text-secondary)]">
            {mapStats.totalDevices} Total Devices
          </Badge>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2">
          {/* Quick Status Legend Chips */}
          <div className="hidden lg:flex items-center gap-3 px-3 py-1.5 rounded-md bg-[var(--elevated)] border border-[var(--border)] text-xs mr-2">
            <span className="flex items-center gap-1.5 text-[var(--text-secondary)]">
              <span className="h-2 w-2 rounded-full bg-[var(--status-online)]" />
              Online ({mapStats.online})
            </span>
            <span className="flex items-center gap-1.5 text-[var(--text-secondary)]">
              <span className="h-2 w-2 rounded-full bg-[var(--status-warning)]" />
              Warning ({mapStats.warning})
            </span>
            <span className="flex items-center gap-1.5 text-[var(--text-secondary)]">
              <span className="h-2 w-2 rounded-full bg-[var(--status-critical)]" />
              Critical ({mapStats.critical})
            </span>
            <span className="flex items-center gap-1.5 text-[var(--text-secondary)]">
              <span className="h-2 w-2 rounded-full bg-[var(--status-offline)]" />
              Offline ({mapStats.offline})
            </span>
          </div>

          <Button
            size="sm"
            variant={clusteringEnabled ? 'primary' : 'secondary'}
            onClick={() => setClusteringEnabled(!clusteringEnabled)}
            title="Toggle marker clustering"
          >
            <Layers className="h-3.5 w-3.5" />
            Clustering {clusteringEnabled ? 'On' : 'Off'}
          </Button>

          <Button size="sm" variant="secondary" onClick={handleFitToDevices} title="Reset view zoom">
            <Navigation className="h-3.5 w-3.5" />
            Fit Visible
          </Button>

          <Button
            size="sm"
            variant="secondary"
            onClick={() => setIsFullScreen(!isFullScreen)}
            title="Toggle full screen map"
          >
            {isFullScreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
            {isFullScreen ? 'Exit Full' : 'Full Screen'}
          </Button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Filter Sidebar */}
        <div className="w-80 shrink-0 border-r border-[var(--border)] bg-[var(--panel)] flex flex-col overflow-hidden">
          {/* Filter Controls */}
          <div className="p-4 border-b border-[var(--border)] space-y-3 shrink-0">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-[var(--text-muted)]" />
              <input
                type="text"
                placeholder="Search site, city, device..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-[var(--elevated)] border border-[var(--border)] rounded-md pl-9 pr-8 py-1.5 text-xs text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--accent)]"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-2.5 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Client Filter */}
            <div>
              <label className="block text-2xs font-medium text-[var(--text-muted)] uppercase mb-1">
                Client
              </label>
              <select
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
                className="w-full bg-[var(--elevated)] border border-[var(--border)] rounded-md px-2.5 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)]"
              >
                <option value="all">All Clients ({clients.length})</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter Tabs */}
            <div>
              <label className="block text-2xs font-medium text-[var(--text-muted)] uppercase mb-1">
                Status Filter
              </label>
              <div className="grid grid-cols-5 gap-1 bg-[var(--elevated)] p-1 rounded-md border border-[var(--border)] text-2xs text-center">
                {['all', 'online', 'warning', 'critical', 'offline'].map((st) => (
                  <button
                    key={st}
                    onClick={() => setStatusFilter(st)}
                    className={cn(
                      'py-1 rounded capitalize transition-colors font-medium',
                      statusFilter === st
                        ? 'bg-[var(--panel)] text-[var(--text-primary)] shadow-sm'
                        : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
                    )}
                  >
                    {st === 'all' ? 'All' : st.slice(0, 3)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Sites List */}
          <div className="flex-1 overflow-y-auto divide-y divide-[var(--border)]">
            {filteredSites.length === 0 ? (
              <div className="p-6 text-center text-xs text-[var(--text-muted)]">
                No sites match the current filters.
              </div>
            ) : (
              filteredSites.map((site) => {
                const info = siteDeviceMap.get(site.id);
                const isSelected = selectedSiteId === site.id;
                const statusColor = getStatusColor(info?.worstStatus || 'online');

                return (
                  <div
                    key={site.id}
                    onClick={() => setSelectedSiteId(site.id)}
                    onMouseEnter={() => setHoveredSiteId(site.id)}
                    onMouseLeave={() => setHoveredSiteId(null)}
                    className={cn(
                      'p-3 cursor-pointer transition-colors flex items-start justify-between gap-2',
                      isSelected
                        ? 'bg-[var(--elevated)] border-l-2 border-l-[var(--accent)]'
                        : 'hover:bg-[var(--hover)]'
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span
                          className="h-2 w-2 rounded-full shrink-0"
                          style={{ backgroundColor: statusColor }}
                        />
                        <h4 className="text-xs font-medium text-[var(--text-primary)] truncate">
                          {site.name}
                        </h4>
                      </div>
                      <p className="text-2xs text-[var(--text-muted)] mt-0.5 truncate">
                        {site.city}, {site.country}
                      </p>

                      {/* Micro Status Badges */}
                      <div className="flex items-center gap-2 mt-2 text-2xs text-[var(--text-muted)]">
                        <span>{info?.devices.length || site.deviceCount} devices</span>
                        <span>•</span>
                        <span className="text-[var(--status-online)]">
                          {info?.counts.online || 0} online
                        </span>
                        {(info?.counts.warning || 0) > 0 && (
                          <span className="text-[var(--status-warning)]">
                            {info?.counts.warning} warn
                          </span>
                        )}
                        {(info?.counts.critical || 0) > 0 && (
                          <span className="text-[var(--status-critical)]">
                            {info?.counts.critical} crit
                          </span>
                        )}
                      </div>
                    </div>

                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 text-[var(--text-muted)] hover:text-[var(--text-primary)] shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenSiteModal(site);
                      }}
                      title="Site details"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Center Map View (70% or flex-1) */}
        <div className="flex-1 relative bg-[var(--panel)] overflow-hidden flex flex-col">
          {/* Zoom controls float */}
          <div className="absolute top-4 right-4 z-20 flex flex-col gap-1 bg-[var(--elevated)] border border-[var(--border)] rounded-md p-1 shadow-md">
            <button
              onClick={() => setZoomLevel((z) => Math.min(3, z + 0.25))}
              className="p-1.5 text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--hover)] rounded transition-colors"
              title="Zoom In"
            >
              <ZoomIn className="h-4 w-4" />
            </button>
            <button
              onClick={() => setZoomLevel((z) => Math.max(0.75, z - 0.25))}
              className="p-1.5 text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--hover)] rounded transition-colors"
              title="Zoom Out"
            >
              <ZoomOut className="h-4 w-4" />
            </button>
            <button
              onClick={() => setZoomLevel(1)}
              className="p-1.5 text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--hover)] rounded transition-colors border-t border-[var(--border)] mt-1 pt-1"
              title="Reset Zoom"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Interactive World Map Canvas Container */}
          <div
            ref={mapRef}
            className="relative flex-1 w-full h-full overflow-hidden select-none bg-[var(--panel)]"
            style={{
              backgroundImage: showGrid
                ? `linear-gradient(to right, rgba(255, 255, 255, 0.03) 1px, transparent 1px),
                   linear-gradient(to bottom, rgba(255, 255, 255, 0.03) 1px, transparent 1px)`
                : 'none',
              backgroundSize: '40px 40px',
            }}
          >
            {/* World Map SVG Vector Overlay */}
            <div
              className="absolute inset-0 transition-transform duration-300 ease-out origin-center pointer-events-none"
              style={{ transform: `scale(${zoomLevel})` }}
            >
              <svg
                viewBox="0 0 1000 500"
                className="w-full h-full opacity-20 text-[var(--text-muted)] fill-current stroke-[var(--border)] stroke-[0.5]"
                preserveAspectRatio="none"
              >
                {/* Equator & Latitude Lines */}
                <line x1="0" y1="250" x2="1000" y2="250" stroke="currentColor" strokeDasharray="4 4" strokeWidth="0.5" />
                <line x1="0" y1="125" x2="1000" y2="125" stroke="currentColor" strokeDasharray="2 2" strokeWidth="0.3" />
                <line x1="0" y1="375" x2="1000" y2="375" stroke="currentColor" strokeDasharray="2 2" strokeWidth="0.3" />

                {/* Simplified Continents Silhouette Paths */}
                {/* North America */}
                <path d="M 120,60 L 280,50 L 320,120 L 260,210 L 190,200 L 160,160 L 100,120 Z" />
                {/* South America */}
                <path d="M 270,230 L 360,250 L 350,380 L 300,450 L 270,360 L 260,280 Z" />
                {/* Europe */}
                <path d="M 460,60 L 580,50 L 600,120 L 530,160 L 470,140 L 450,90 Z" />
                {/* Africa */}
                <path d="M 450,160 L 590,160 L 620,260 L 560,390 L 480,380 L 440,260 Z" />
                {/* Asia */}
                <path d="M 600,50 L 920,40 L 940,200 L 820,260 L 700,220 L 610,130 Z" />
                {/* Australia */}
                <path d="M 800,320 L 920,310 L 930,410 L 820,420 Z" />
              </svg>
            </div>

            {/* Site Markers Layer */}
            <div
              className="absolute inset-0 transition-transform duration-300 ease-out origin-center"
              style={{ transform: `scale(${zoomLevel})` }}
            >
              {clusters.map((cluster) => {
                const isSingleSite = cluster.sites.length === 1;
                const site = cluster.sites[0];
                const isSelected = isSingleSite && selectedSiteId === site.id;
                const isHovered = isSingleSite && hoveredSiteId === site.id;
                const statusColor = getStatusColor(cluster.worstStatus);

                return (
                  <div
                    key={cluster.id}
                    className="absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer group z-10"
                    style={{ left: `${cluster.xPct}%`, top: `${cluster.yPct}%` }}
                    onClick={() => {
                      if (isSingleSite) {
                        setSelectedSiteId(site.id);
                      } else {
                        // Zoom in when clicking cluster
                        setZoomLevel((z) => Math.min(3, z + 0.5));
                      }
                    }}
                    onMouseEnter={() => {
                      if (isSingleSite) setHoveredSiteId(site.id);
                    }}
                    onMouseLeave={() => {
                      if (isSingleSite) setHoveredSiteId(null);
                    }}
                  >
                    {/* Marker Pin Visual */}
                    {isSingleSite ? (
                      <div className="relative flex items-center justify-center">
                        {/* Status Pulse Ring */}
                        {cluster.worstStatus === 'critical' && (
                          <span
                            className="absolute h-8 w-8 rounded-full opacity-50 animate-ping"
                            style={{ backgroundColor: statusColor }}
                          />
                        )}
                        <div
                          className={cn(
                            'h-6 w-6 rounded-full border-2 border-[var(--panel)] flex items-center justify-center transition-transform duration-200 shadow-lg',
                            isSelected && 'scale-125 ring-2 ring-[var(--accent)]'
                          )}
                          style={{ backgroundColor: statusColor }}
                        >
                          <MapPin className="h-3.5 w-3.5 text-white" />
                        </div>

                        {/* Hover Tooltip */}
                        <div
                          className={cn(
                            'absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-[var(--elevated)] border border-[var(--border)] rounded-md px-3 py-2 text-xs shadow-xl pointer-events-none transition-opacity duration-150 whitespace-nowrap z-30',
                            isHovered || isSelected ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                          )}
                        >
                          <div className="font-semibold text-[var(--text-primary)]">{site.name}</div>
                          <div className="text-2xs text-[var(--text-muted)] mt-0.5">
                            {site.city}, {site.country} • {cluster.totalDevices} devices
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-2xs">
                            <span className="text-[var(--status-online)]">
                              {cluster.statusCounts.online || 0} online
                            </span>
                            {(cluster.statusCounts.warning || 0) > 0 && (
                              <span className="text-[var(--status-warning)]">
                                {cluster.statusCounts.warning} warn
                              </span>
                            )}
                            {(cluster.statusCounts.critical || 0) > 0 && (
                              <span className="text-[var(--status-critical)]">
                                {cluster.statusCounts.critical} crit
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Cluster Marker Pin */
                      <div className="relative flex items-center justify-center">
                        <div
                          className="h-8 w-8 rounded-full border-2 border-white/20 text-white font-bold text-xs flex items-center justify-center shadow-xl transition-transform hover:scale-110"
                          style={{ backgroundColor: statusColor }}
                        >
                          {cluster.sites.length}
                        </div>
                        {/* Cluster Tooltip */}
                        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-[var(--elevated)] border border-[var(--border)] rounded-md px-2.5 py-1.5 text-2xs shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-30">
                          <span className="font-semibold text-[var(--text-primary)]">
                            {cluster.sites.length} Sites Cluster
                          </span>
                          <span className="block text-[var(--text-muted)]">
                            {cluster.totalDevices} Devices Total (Click to zoom)
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom Bar Map Info Overlay */}
          <div className="px-4 py-2 bg-[var(--panel)] border-t border-[var(--border)] flex items-center justify-between text-xs text-[var(--text-muted)] z-10 shrink-0">
            <div className="flex items-center gap-4">
              <span>Projection: Equirectangular</span>
              <span>Coordinates System: WGS 84</span>
              <span>Zoom level: {Math.round(zoomLevel * 100)}%</span>
            </div>
            <div>Showing {clusters.length} active site markers</div>
          </div>
        </div>

        {/* Right Split View Sidebar: Selected Site Devices (30%) */}
        <div className="w-96 shrink-0 border-l border-[var(--border)] bg-[var(--panel)] flex flex-col overflow-hidden">
          {selectedSite ? (
            <>
              {/* Selected Site Header Details */}
              <div className="p-4 border-b border-[var(--border)] bg-[var(--elevated)] shrink-0">
                <div className="flex items-start justify-between">
                  <div>
                    <span className="text-2xs uppercase tracking-wider font-semibold text-[var(--accent)]">
                      Selected Location
                    </span>
                    <h2 className="text-sm font-semibold text-[var(--text-primary)] mt-0.5">
                      {selectedSite.name}
                    </h2>
                    <p className="text-xs text-[var(--text-muted)] mt-0.5 flex items-center gap-1">
                      <Building2 className="h-3.5 w-3.5" />
                      {selectedSite.address}, {selectedSite.city}, {selectedSite.country}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleOpenSiteModal(selectedSite)}
                    title="View Full Site Modal"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Button>
                </div>

                {/* Status breakdown bar */}
                <div className="mt-3">
                  <div className="flex justify-between text-2xs text-[var(--text-muted)] mb-1">
                    <span>Device Health Distribution</span>
                    <span>{selectedSiteDevices.length} Total</span>
                  </div>
                  <div className="h-2 w-full bg-[var(--border)] rounded-full overflow-hidden flex">
                    {(() => {
                      const counts = siteDeviceMap.get(selectedSite.id)?.counts || {
                        online: 0,
                        warning: 0,
                        critical: 0,
                        offline: 0,
                      };
                      const total = selectedSiteDevices.length || 1;
                      return (
                        <>
                          <div
                            style={{ width: `${(counts.online / total) * 100}%` }}
                            className="bg-[var(--status-online)] h-full"
                          />
                          <div
                            style={{ width: `${(counts.warning / total) * 100}%` }}
                            className="bg-[var(--status-warning)] h-full"
                          />
                          <div
                            style={{ width: `${(counts.critical / total) * 100}%` }}
                            className="bg-[var(--status-critical)] h-full"
                          />
                          <div
                            style={{ width: `${(counts.offline / total) * 100}%` }}
                            className="bg-[var(--status-offline)] h-full"
                          />
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Devices Header */}
              <div className="px-4 py-2 border-b border-[var(--border)] bg-[var(--panel)] flex items-center justify-between shrink-0">
                <span className="text-xs font-medium text-[var(--text-secondary)]">
                  Devices at Site ({selectedSiteDevices.length})
                </span>
              </div>

              {/* Device List */}
              <div className="flex-1 overflow-y-auto divide-y divide-[var(--border)]">
                {selectedSiteDevices.length === 0 ? (
                  <div className="p-8 text-center text-xs text-[var(--text-muted)]">
                    No devices currently enrolled at this site.
                  </div>
                ) : (
                  selectedSiteDevices.map((dev) => (
                    <div
                      key={dev.id}
                      onClick={() => navigate(`/devices/${dev.id}`)}
                      className="p-3.5 hover:bg-[var(--hover)] transition-colors cursor-pointer group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <Laptop className="h-4 w-4 text-[var(--text-muted)] group-hover:text-[var(--accent)] shrink-0 transition-colors" />
                          <span className="text-xs font-semibold text-[var(--text-primary)] truncate">
                            {dev.hostname}
                          </span>
                        </div>
                        <Badge variant={dev.status} dot>
                          {dev.status}
                        </Badge>
                      </div>

                      <div className="mt-2 grid grid-cols-2 gap-2 text-2xs text-[var(--text-muted)]">
                        <div>
                          <span className="text-[var(--text-muted)]">IP:</span>{' '}
                          <span className="text-[var(--text-secondary)]">{dev.publicIp}</span>
                        </div>
                        <div>
                          <span className="text-[var(--text-muted)]">OS:</span>{' '}
                          <span className="text-[var(--text-secondary)] truncate">{dev.os}</span>
                        </div>
                        <div>
                          <span className="text-[var(--text-muted)]">CPU:</span>{' '}
                          <span className="text-[var(--text-secondary)]">{formatPercent(dev.cpu)}</span>
                        </div>
                        <div>
                          <span className="text-[var(--text-muted)]">RAM:</span>{' '}
                          <span className="text-[var(--text-secondary)]">{formatPercent(dev.memory)}</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <div className="p-8 text-center text-xs text-[var(--text-muted)]">
              Select a site marker to inspect its devices.
            </div>
          )}
        </div>
      </div>

      {/* Detailed Site Modal */}
      {modalSite && (
        <Modal
          open={siteModalOpen}
          onClose={() => setSiteModalOpen(false)}
          title={`Site Details: ${modalSite.name}`}
          size="lg"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div className="p-3 rounded-md bg-[var(--elevated)] border border-[var(--border)]">
                <span className="text-2xs text-[var(--text-muted)] uppercase block">Location</span>
                <span className="font-medium text-[var(--text-primary)]">
                  {modalSite.address}, {modalSite.city}, {modalSite.country}
                </span>
              </div>
              <div className="p-3 rounded-md bg-[var(--elevated)] border border-[var(--border)]">
                <span className="text-2xs text-[var(--text-muted)] uppercase block">Timezone</span>
                <span className="font-medium text-[var(--text-primary)]">{modalSite.timezone}</span>
              </div>
              <div className="p-3 rounded-md bg-[var(--elevated)] border border-[var(--border)]">
                <span className="text-2xs text-[var(--text-muted)] uppercase block">Coordinates</span>
                <span className="font-medium text-[var(--text-primary)]">
                  Lat: {modalSite.lat.toFixed(4)}, Lng: {modalSite.lng.toFixed(4)}
                </span>
              </div>
              <div className="p-3 rounded-md bg-[var(--elevated)] border border-[var(--border)]">
                <span className="text-2xs text-[var(--text-muted)] uppercase block">Device Count</span>
                <span className="font-medium text-[var(--text-primary)]">
                  {siteDeviceMap.get(modalSite.id)?.devices.length || modalSite.deviceCount} Devices
                </span>
              </div>
            </div>

            <div>
              <h4 className="text-xs font-semibold text-[var(--text-primary)] mb-2">Enrolled Hardware</h4>
              <div className="max-h-60 overflow-y-auto border border-[var(--border)] rounded-md divide-y divide-[var(--border)]">
                {(siteDeviceMap.get(modalSite.id)?.devices || []).map((d) => (
                  <div key={d.id} className="p-2.5 flex items-center justify-between text-xs">
                    <div>
                      <span className="font-medium text-[var(--text-primary)]">{d.hostname}</span>
                      <span className="text-2xs text-[var(--text-muted)] ml-2">{d.os}</span>
                    </div>
                    <Badge variant={d.status}>{d.status}</Badge>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
